import { Link, useParams } from "react-router-dom";
import { useAccount } from "wagmi";
import { isAddress, type Address } from "viem";
import { byShortName } from "@/lib/chains";
import { ExplorerLink } from "@/components/ExplorerLink";
import { ArtworkImage } from "@/components/ArtworkImage";
import { ipfsToHttp } from "@/lib/ipfs";
import { useTokenMetadata } from "@/hooks/useTokenMetadata";
import { TraitsGrid } from "@/components/tokendetail/TraitsGrid";
import { TransferForm } from "@/components/tokendetail/TransferForm";

/**
 * Per-token detail page. Owns URL parsing + early-return states; the on-
 * chain reads + JSON fetch live in useTokenMetadata, and the trait grid
 * + transfer form are their own components.
 */
export function TokenDetail() {
  const { shortName, address: addrParam, tokenId: tokenIdParam } = useParams<{
    shortName: string;
    address: string;
    tokenId: string;
  }>();
  const chain = shortName ? byShortName(shortName) : undefined;
  const address = (addrParam ?? "").toLowerCase() as Address;
  const tokenIdNum = Number(tokenIdParam);
  const valid =
    !!chain && isAddress(address) && Number.isInteger(tokenIdNum) && tokenIdNum >= 1;

  const { address: wallet } = useAccount();
  const ipfsGateway = chain?.ipfsGateway ?? "https://dweb.link/ipfs/";

  const meta = useTokenMetadata({
    address,
    chainId: chain?.chainId ?? 0,
    tokenId: tokenIdNum,
    ipfsGateway,
    enabled: valid,
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">Token not found</h1>
        <p className="text-sm text-muted-foreground">
          The URL <code>/{shortName}/{addrParam}/{tokenIdParam}</code> isn't a valid token.
        </p>
      </div>
    );
  }

  if (meta.tokenIdMissing) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">#{tokenIdNum} doesn't exist yet</h1>
        <p className="text-sm text-muted-foreground">
          This token hasn't been minted on this collection.
        </p>
        <Link to={`/${shortName}/${address}`} className="btn-secondary mt-4 inline-block">
          Back to collection
        </Link>
      </div>
    );
  }

  const isOwner = !!wallet && !!meta.owner && meta.owner === wallet.toLowerCase();
  const img = meta.metadata?.image ? ipfsToHttp(meta.metadata.image, ipfsGateway) : null;
  const displayName = meta.metadata?.name ?? `${meta.collectionName ?? "Token"} #${tokenIdNum}`;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="mb-4 text-xs text-muted-foreground">
        <Link to={`/${shortName}/${address}`} className="hover:text-foreground">
          ← {meta.collectionName ?? "Collection"}
        </Link>
      </div>

      <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_360px] md:items-start">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className="relative aspect-square w-full bg-muted">
            {img ? (
              <ArtworkImage src={img} alt={displayName} className="h-full w-full object-cover" />
            ) : meta.metaLoading ? (
              <div className="absolute inset-0 animate-pulse bg-muted" aria-hidden="true" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                #{tokenIdNum}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
            #{tokenIdNum}
          </div>
          {meta.metadata ? (
            <h1 className="mb-3 text-2xl font-bold">{displayName}</h1>
          ) : meta.metaLoading ? (
            <div className="mb-3 h-7 w-3/4 animate-pulse rounded bg-muted" aria-hidden="true" />
          ) : (
            <h1 className="mb-3 text-2xl font-bold">{displayName}</h1>
          )}
          {meta.metadata?.description ? (
            <p className="mb-4 text-sm text-muted-foreground">{meta.metadata.description}</p>
          ) : meta.metaLoading ? (
            <div className="mb-4 space-y-1.5" aria-hidden="true">
              <div className="h-3 w-full animate-pulse rounded bg-muted" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
            </div>
          ) : null}
          {!meta.revealed && (
            <div className="mb-4 rounded-md border border-violet-300 bg-violet-50 p-2 text-xs text-violet-900 dark:border-violet-700/40 dark:bg-violet-900/20 dark:text-violet-200">
              🎭 Pre-reveal — traits will appear after the creator reveals the collection.
            </div>
          )}
          {meta.metaError && (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-300">
              Couldn't load metadata for this token — the collection's baseURI may be unset or unreachable.
            </div>
          )}

          <dl className="mb-6 space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">Chain</dt>
              <dd>{chain.name}</dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">Contract</dt>
              <dd>
                <ExplorerLink chain={chain} kind="address" value={address} />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-2">
              <dt className="text-muted-foreground">Owner</dt>
              <dd>
                {meta.owner ? (
                  isOwner ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="rounded-md bg-emerald-500 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-950">
                        you
                      </span>
                      <ExplorerLink chain={chain} kind="address" value={meta.owner} />
                    </span>
                  ) : (
                    <ExplorerLink chain={chain} kind="address" value={meta.owner} />
                  )
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </dd>
            </div>
            {meta.tokenUri && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">Metadata</dt>
                <dd>
                  <a
                    href={meta.httpUri ?? meta.tokenUri}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-foreground"
                  >
                    JSON ↗
                  </a>
                </dd>
              </div>
            )}
            {meta.metadata?.external_url && (
              <div className="flex items-center justify-between gap-2">
                <dt className="text-muted-foreground">External</dt>
                <dd>
                  <a
                    href={meta.metadata.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-foreground"
                  >
                    Link ↗
                  </a>
                </dd>
              </div>
            )}
          </dl>

          <TraitsGrid
            metadata={meta.metadata ?? null}
            metaLoading={meta.metaLoading}
            revealed={meta.revealed}
          />

          {isOwner && (
            <TransferForm
              chain={chain}
              contractAddress={address}
              tokenId={tokenIdNum}
              onTransferred={() => void meta.refetch()}
            />
          )}
        </div>
      </div>
    </div>
  );
}
